import { Section, CodeBlock, InfoBox } from "@/components"

export function ReactTailwindStatePage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">State Management</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn how state management works in this template. We use Zustand for lightweight, 
          type-safe global state management with minimal boilerplate.
        </p>
      </div>

      <Section title="Why Zustand?" description="Lightweight and powerful">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Minimal Boilerplate</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              No providers, reducers, or actions. Create stores with simple functions and use 
              them anywhere in your app with hooks.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">TypeScript First</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Full TypeScript support with automatic type inference. Your IDE will autocomplete 
              state and actions.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">DevTools Integration</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Debug your state changes with Redux DevTools. Track actions and inspect state 
              mutations easily.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Persistence Built-in</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Persist state to localStorage or sessionStorage with a simple middleware. No 
              additional libraries needed.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Store Organization" description="How stores are structured">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template includes three main stores, each focused on a specific domain:
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">auth.store.ts</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Manages authentication state including user data, tokens, and auth status.
              </p>
            </div>
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">theme.store.ts</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Handles theme preferences (dark/light mode) with system preference detection.
              </p>
            </div>
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">ui.store.ts</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Manages UI state like sidebar collapse, notifications, and modals.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Auth Store" description="Authentication state management">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The auth store manages user authentication state with automatic persistence to localStorage.
          </p>
          
          <CodeBlock
            code={`import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import type { User } from "@/types/auth.types"

interface AuthState {
  // State
  user: User | null
  token: string | null
  isAuthenticated: boolean
  
  // Actions
  setAuth: (user: User, token: string) => void
  updateUser: (user: Partial<User>) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      
      // Set authentication (login)
      setAuth: (user, token) => {
        localStorage.setItem("token", token)
        set({ 
          user, 
          token, 
          isAuthenticated: true 
        })
      },
      
      // Update user data
      updateUser: (userData) => {
        const currentUser = get().user
        if (currentUser) {
          set({ 
            user: { ...currentUser, ...userData } 
          })
        }
      },
      
      // Clear authentication (logout)
      clearAuth: () => {
        localStorage.removeItem("token")
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false 
        })
      }
    }),
    {
      name: "auth-storage", // localStorage key
      storage: createJSONStorage(() => localStorage)
    }
  )
)`}
            language="typescript"
            filename="src/store/auth.store.ts"
          />

          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold">Using the Auth Store</h3>
            <CodeBlock
              code={`import { useAuthStore } from "@/store/auth.store"

function UserProfile() {
  // Select specific state
  const user = useAuthStore((state) => state.user)
  const clearAuth = useAuthStore((state) => state.clearAuth)
  
  // Or select multiple values
  const { user, isAuthenticated } = useAuthStore()

  const handleLogout = () => {
    clearAuth()
    // Additional logout logic...
  }

  if (!isAuthenticated) {
    return <p>Please login</p>
  }

  return (
    <div>
      <p>Welcome, {user?.name}</p>
      <button onClick={handleLogout}>Logout</button>
    </div>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Theme Store" description="Dark/light mode management">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The theme store handles theme switching with automatic system preference detection.
          </p>
          
          <CodeBlock
            code={`import { create } from "zustand"
import { persist } from "zustand/middleware"

type Theme = "light" | "dark" | "system"

interface ThemeState {
  // State
  theme: Theme
  
  // Actions
  setTheme: (theme: Theme) => void
  getEffectiveTheme: () => "light" | "dark"
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: "system",
      
      setTheme: (theme) => {
        set({ theme })
        
        // Apply theme to document
        const root = window.document.documentElement
        root.classList.remove("light", "dark")
        
        if (theme === "system") {
          const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          root.classList.add(systemTheme)
        } else {
          root.classList.add(theme)
        }
      },
      
      getEffectiveTheme: () => {
        const { theme } = get()
        
        if (theme === "system") {
          return window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
        }
        
        return theme
      }
    }),
    {
      name: "theme-storage"
    }
  )
)

// Initialize theme on app load
if (typeof window !== "undefined") {
  const theme = useThemeStore.getState().theme
  useThemeStore.getState().setTheme(theme)
}`}
            language="typescript"
            filename="src/store/theme.store.ts"
          />

          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold">Using the Theme Store</h3>
            <CodeBlock
              code={`import { useThemeStore } from "@/store/theme.store"
import { Moon, Sun, Monitor } from "lucide-react"

function ThemeSwitcher() {
  const { theme, setTheme } = useThemeStore()

  return (
    <div className="flex gap-2">
      <button
        onClick={() => setTheme("light")}
        className={\`p-2 rounded-lg \${
          theme === "light" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700"
        }\`}
      >
        <Sun className="h-4 w-4" />
      </button>
      <button
        onClick={() => setTheme("dark")}
        className={\`p-2 rounded-lg \${
          theme === "dark" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700"
        }\`}
      >
        <Moon className="h-4 w-4" />
      </button>
      <button
        onClick={() => setTheme("system")}
        className={\`p-2 rounded-lg \${
          theme === "system" 
            ? "bg-blue-600 text-white" 
            : "bg-gray-200 dark:bg-gray-700"
        }\`}
      >
        <Monitor className="h-4 w-4" />
      </button>
    </div>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="UI Store" description="Application UI state">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The UI store manages application-wide UI state like sidebar visibility and notifications.
          </p>
          
          <CodeBlock
            code={`import { create } from "zustand"

interface Notification {
  id: string
  title: string
  message: string
  type: "info" | "success" | "warning" | "error"
  timestamp: number
}

interface UIState {
  // State
  sidebarCollapsed: boolean
  notifications: Notification[]
  activeModal: string | null
  
  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  addNotification: (notification: Omit<Notification, "id" | "timestamp">) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
  openModal: (modalId: string) => void
  closeModal: () => void
}

export const useUIStore = create<UIState>((set) => ({
  // Initial state
  sidebarCollapsed: false,
  notifications: [],
  activeModal: null,
  
  // Sidebar actions
  toggleSidebar: () => 
    set((state) => ({ 
      sidebarCollapsed: !state.sidebarCollapsed 
    })),
  
  setSidebarCollapsed: (collapsed) => 
    set({ sidebarCollapsed: collapsed }),
  
  // Notification actions
  addNotification: (notification) => 
    set((state) => ({
      notifications: [
        ...state.notifications,
        {
          ...notification,
          id: Math.random().toString(36).substr(2, 9),
          timestamp: Date.now()
        }
      ]
    })),
  
  removeNotification: (id) => 
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id)
    })),
  
  clearNotifications: () => 
    set({ notifications: [] }),
  
  // Modal actions
  openModal: (modalId) => 
    set({ activeModal: modalId }),
  
  closeModal: () => 
    set({ activeModal: null })
}))`}
            language="typescript"
            filename="src/store/ui.store.ts"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">State Management Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep stores focused on a single domain (auth, theme, etc.)</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use TypeScript interfaces for type-safe state and actions</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Select only the state you need to prevent unnecessary re-renders</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use persist middleware for state that should survive page refreshes</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep computed values as actions for better performance</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

